import { isEmpty } from 'lodash-es'
import { action, computed, makeObservable, observable } from 'mobx'

import { AddressTypeEnum } from 'Modules/order/components/OrderAddressForm/OrderAddressForm.types'
import { OrderFlowTypesEnum } from 'Modules/order/types/orderFlow.types'

import { DEVICE_TYPE, PAGINATION } from 'Constants/constants'
import { mutationNewDeviceCaseItem_createNewDeviceCaseItem_subCaseItems } from 'Constants/graphql/mutations/__generated__/mutationNewDeviceCaseItem'
import { getEmployee_employee_office_address } from 'Constants/graphql/queries/employee/__generated__/getEmployee'
import {
  getEmployeeContact,
  getEmployeeContact_employee_contact,
  getEmployeeContactVariables,
} from 'Constants/graphql/queries/employee/__generated__/getEmployeeContact'
import { GET_EMPLOYEE_CONTACT } from 'Constants/graphql/queries/employee/getEmployeeContact'
import {
  getCatalog,
  getCatalogVariables,
} from 'Constants/graphql/queries/feature/__generated__/getCatalog'
import {
  getDeviceAccessories,
  getDeviceAccessoriesVariables,
} from 'Constants/graphql/queries/feature/__generated__/getDeviceAccessories'
import {
  getOrderEmployee_employee_limitationRules_limitationRule,
  getOrderEmployee_employee_office_address,
} from 'Constants/graphql/queries/feature/__generated__/getOrderEmployee'
import { getOrderOrganisation_organisation_offices_offices_address } from 'Constants/graphql/queries/feature/__generated__/getOrderOrganisation'
import { GET_CATALOG } from 'Constants/graphql/queries/feature/GetCatalog'
import { GET_DEVICE_ACCESSORIES } from 'Constants/graphql/queries/feature/GetDeviceAccessories'
import { getOrganisation_organisation_contact_address } from 'Constants/graphql/queries/organisation/__generated__/getOrganisation'

import ApolloLoader from 'Portal/src/ApolloLoader'
import {
  productOffersDeviceMapper,
  rentCategoriesDeviceMapper,
} from 'Portal/src/mappers/orderDeviceMapper/orderDevice.mapper'
import {
  OrderDevice,
  OrderDeviceMappedResponse,
} from 'Portal/src/mappers/orderDeviceMapper/orderDevice.types'
import { calculateNetPrices } from 'Portal/src/utilities/calculateNetPrices/calculateNetPrices.util'

import type { RootStore } from '..'
import { NetPriceCalculatorDefaultValues } from './orderStore.constants'
import {
  NetPriceCalculatorInput,
  OrderFilters,
  OrderFilterTypeEnum,
  OrderLimitations,
  OrderSortCriteriaEnum,
  OrderStepEnum,
  OriginRoute,
} from './orderStore.types'
import {
  adjustChosenAccessoriesLimitationRules,
  adjustChosenAccessoryLimitationRules,
  adjustChosenDeviceLimitationRules,
} from './orderStore.utils'

export class OrderStore {
  currentStep: OrderStepEnum | null = null

  orderDevices: OrderDeviceMappedResponse | null = null

  chosenDevice: OrderDevice | null = null

  chosenAccessories: Map<string, OrderDevice> = new Map()

  chosenShippingAddress:
    | getOrderEmployee_employee_office_address
    | getOrderOrganisation_organisation_offices_offices_address
    | null = null

  chosenShippingAddressLabel: string | null = null

  chosenShippingAddressType: AddressTypeEnum | null = null

  remarks: string | null = null

  filters: OrderFilters = new Map([
    [OrderFilterTypeEnum.Colors, new Set<string>()],
    [OrderFilterTypeEnum.Manufacturers, new Set<string>()],
    [OrderFilterTypeEnum.Storages, new Set<string>()],
    [OrderFilterTypeEnum.ProductTypes, new Set<string>()],
  ])

  sortCriteria: OrderSortCriteriaEnum = OrderSortCriteriaEnum.Default

  accessories: OrderDevice[] | null = null

  officeAddress:
    | getOrganisation_organisation_contact_address
    | getEmployee_employee_office_address
    | null = null

  fetchError: boolean = false

  mutationError = false

  isAccessoriesLoading = false

  isCYOD = false

  isLoading = true

  isProductOffer = false

  isOrderDisabled = false

  orderedDevices:
    | mutationNewDeviceCaseItem_createNewDeviceCaseItem_subCaseItems[]
    | number
    | null = null

  netPriceCalculator: NetPriceCalculatorInput = NetPriceCalculatorDefaultValues

  limitationRules: OrderLimitations | null = null

  employeeContact: getEmployeeContact_employee_contact | null = null

  originRoute: OriginRoute = null

  previewColumnDevice: OrderDevice | null = null

  constructor(private readonly store: RootStore) {
    makeObservable(this, {
      accessories: observable,
      chosenAccessories: observable,
      chosenDevice: observable,
      chosenShippingAddress: observable,
      chosenShippingAddressLabel: observable,
      chosenShippingAddressType: observable,
      clearAllChoices: action,
      clearChosenAccessories: action,
      clearChosenDeviceLimitationRules: action,
      clearChosenDeviceWithAccessories: action,
      clearFilters: action,
      createLimitationRules: action,
      currentStep: observable,
      employeeContact: observable,
      fetchCatalog: action,
      fetchEmployeeContact: action,
      fetchError: observable,
      fetchOrderAccessories: action,
      filters: observable,
      getLimitationRules: action,
      getOrderAddress: action,
      getTotalPrice: computed,
      isAccessoriesLoading: observable,
      isCYOD: observable,
      isLoading: observable,
      isOrderDisabled: observable,
      limitationRules: observable,
      mutationError: observable,
      netPriceCalculator: observable,
      officeAddress: observable,
      orderDevices: observable,
      orderedDevices: observable,
      originRoute: observable,
      previewColumnDevice: observable,
      remarks: observable,
      resetAll: action,
      setAccessories: action,
      setAccessoryLabel: action,
      setAccessoryQuantity: action,
      setChosenDevice: action,
      setChosenDeviceLabel: action,
      setChosenDeviceQuantity: action,
      setChosenShippingAddress: action,
      setChosenShippingAddressLabel: action,
      setChosenShippingAddressType: action,
      setCurrentStep: action,
      setEmployeeContact: action,
      setFetchError: action,
      setFilters: action,
      setIsCYOD: action,
      setIsLoading: action,
      setIsOrderDisabled: action,
      setLimitationRules: action,
      setMutationError: action,
      setNetPriceCalculator: action,
      setOfficeAddress: action,
      setOrderDevices: action,
      setOrderedDevices: action,
      setOriginRoute: action,
      setRemarks: action,
      setSortCriteria: action,
      shouldSkipAccessories: computed,
      sortCriteria: observable,
      toggleAccessorySelection: action,
    })
  }

  createLimitationRules = (
    limitationRules: getOrderEmployee_employee_limitationRules_limitationRule[],
    upgradeDeviceType?: string | null,
  ) => {
    const OrderLimitations: OrderLimitations = {
      [DEVICE_TYPE.Laptop]: {
        current: 0,
        initial: 0,
      },
      [DEVICE_TYPE.Tablet]: {
        current: 0,
        initial: 0,
      },
      [DEVICE_TYPE.Smartphone]: {
        current: 0,
        initial: 0,
      },
      [DEVICE_TYPE.Other]: {
        current: 0,
        initial: 0,
      },
    }

    for (const limitation of limitationRules) {
      if (
        limitation.type !== DEVICE_TYPE.Laptop &&
        limitation.type !== DEVICE_TYPE.Tablet &&
        limitation.type !== DEVICE_TYPE.Smartphone
      ) {
        OrderLimitations.other.initial += limitation.available
        OrderLimitations.other.current += limitation.available
      } else {
        OrderLimitations[limitation.type as keyof OrderLimitations].initial +=
          limitation.available

        OrderLimitations[limitation.type as keyof OrderLimitations].current +=
          limitation.available
      }
    }

    if (upgradeDeviceType) {
      OrderLimitations[upgradeDeviceType as keyof OrderLimitations].initial += 1
      OrderLimitations[upgradeDeviceType as keyof OrderLimitations].current += 1
    }

    this.setLimitationRules(OrderLimitations)
  }

  getOrderAddress = (isOrganisation: boolean, isEmployee: boolean) => {
    const { organisation } = this.store.organisationStore
    const { employee } = this.store.employeeStore
    const { portalConfig } = this.store.portalConfigStore

    const organisationAddress = organisation?.contact?.address
    const employeeAddress =
      employee?.office?.address || employee?.organisation?.contact?.address

    const address =
      isOrganisation && !isEmployee ? organisationAddress : employeeAddress

    if (!address && !portalConfig?.shipDeviceToCustomAddress) {
      this.setFetchError(true)
      this.setIsLoading(false)

      return
    }

    if (address) {
      this.setOfficeAddress(address)
    }

    this.setIsLoading(false)
  }

  getLimitationRules = (upgradeDeviceType?: string | null) => {
    const { organisation } = this.store.organisationStore
    const { employee } = this.store.employeeStore

    const isEmployee = !organisation?.id && employee?.id

    const limitationRules =
      isEmployee && employee.limitationRules?.limitationRule

    if (limitationRules) {
      this.createLimitationRules(limitationRules, upgradeDeviceType)
    }
  }

  fetchCatalog = async (
    orderFlowType: `${OrderFlowTypesEnum}`,
    employeeId: string | undefined,
    isOrganisation: boolean,
  ) => {
    const { apolloClient } = ApolloLoader
    const { organisation } = this.store.organisationStore
    const { employee } = this.store.employeeStore

    const isEmployee = !!(
      (!isOrganisation && employee?.id) ||
      (isOrganisation && employeeId)
    )

    const organisationId =
      isOrganisation && !isEmployee
        ? organisation?.id.toString()
        : employee?.organisation?.id.toString() || organisation?.id.toString()

    if (!apolloClient) return

    this.setIsLoading(true)
    this.setIsOrderDisabled(false)

    try {
      const { rentalDetails } = this.store.rentalStore
      const { portalConfig } = this.store.portalConfigStore

      if (organisationId) {
        const shouldUseLimitationRules =
          (orderFlowType === OrderFlowTypesEnum.NEW_ORDER && !isOrganisation) ||
          (portalConfig?.enableProductOffers && !isOrganisation) ||
          false

        const onlyShowDevicesWithSameProductType =
          (orderFlowType === OrderFlowTypesEnum.UPGRADE ||
            orderFlowType === OrderFlowTypesEnum.EARLY_UPGRADE) &&
          isEmployee

        const response = await apolloClient.query<
          getCatalog,
          getCatalogVariables
        >({
          query: GET_CATALOG,
          variables: {
            after: PAGINATION.AFTER,
            filter: {
              enableLimitations: shouldUseLimitationRules,
              organisationID: organisationId,
              ...(isEmployee && {
                employeeID: employee?.id.toString() || employeeId,
              }),
              productTypeByDeviceDefinitionID:
                onlyShowDevicesWithSameProductType
                  ? rentalDetails?.rental.stocklistItem?.deviceDefinition?.id
                  : undefined,
            },
            first: PAGINATION.ITEMS_PER_PAGE,
            organisationId: Number(organisationId),
          },
        })

        const { catalog } = response.data

        const disableNewOrderForEmployee =
          isEmployee &&
          orderFlowType === OrderFlowTypesEnum.NEW_ORDER &&
          !catalog?.employee?.actions.showOrderNewDeviceAction

        if (disableNewOrderForEmployee || catalog === null) {
          this.setIsOrderDisabled(true)
        }

        if (catalog?.offers) {
          this.setIsProductOffer(true)
          this.setOrderDevices(
            productOffersDeviceMapper(catalog?.offers.offerConfigGroups),
          )
        } else {
          this.setIsProductOffer(false)
          this.setOrderDevices(
            rentCategoriesDeviceMapper(
              catalog?.rentCategories?.rentcategories || null,
            ),
          )
        }

        this.getOrderAddress(isOrganisation, isEmployee)

        if (orderFlowType !== OrderFlowTypesEnum.NEW_ORDER) {
          this.getLimitationRules(
            rentalDetails?.rental.stocklistItem?.deviceDefinition?.productType,
          )
        } else {
          this.getLimitationRules()
        }
      }
    } catch (error) {
      this.setFetchError(true)
    } finally {
      this.setIsLoading(false)
    }
  }

  fetchOrderAccessories = async () => {
    const { apolloClient } = ApolloLoader

    if (!apolloClient || !this.chosenDevice?.rentCategoryId) return

    this.setIsAccessoriesLoading(true)

    try {
      const response = await apolloClient.query<
        getDeviceAccessories,
        getDeviceAccessoriesVariables
      >({
        query: GET_DEVICE_ACCESSORIES,
        variables: { id: this.chosenDevice.rentCategoryId },
      })

      const deviceDefinitions =
        response.data.rentCategory.deviceDefinitions?.deviceDefinitions
      let fetchedAccessories: OrderDevice[] = []

      if (deviceDefinitions) {
        const accessories = deviceDefinitions.find(
          deviceDefinition =>
            deviceDefinition.id === this.chosenDevice?.deviceDefinition.id,
        )?.accessories

        accessories?.accessories?.forEach((accessory, index) => {
          fetchedAccessories = [
            ...fetchedAccessories,
            {
              basisAmount: accessory.rentCategory.displayedBasisAmount,
              calculatedNet: 0,
              copaymentGross: accessory.rentCategory.copaymentGross,
              copaymentNet: accessory.rentCategory.copaymentNet,
              deviceDefinition: accessory.deviceDefinition,
              forcePriceHide: accessory.rentCategory.forcePriceHide,
              id: index.toString(),
              minimumContractDuration:
                this.chosenDevice?.minimumContractDuration || 0,
              offerId: accessory.rentCategory.id || '',
              quantity: 1,
              rentCategoryId: accessory.rentCategory.id || '',
            },
          ]
        })

        this.setAccessories(fetchedAccessories)
      }

      if (this.netPriceCalculator.salaryValue) {
        calculateNetPrices(this)
      }

      this.setIsAccessoriesLoading(false)
    } catch (error) {
      this.setIsAccessoriesLoading(false)
    }
  }

  setOrderDevices = (orderDevices: OrderDeviceMappedResponse | null) => {
    this.orderDevices = orderDevices
  }

  setChosenDevice = async (device: OrderDevice | null) => {
    const { chosenDevice, limitationRules, setLimitationRules } = this

    if (device) {
      const newLimitationRules = adjustChosenDeviceLimitationRules(
        limitationRules,
        device,
        'remove',
      )

      if (newLimitationRules) {
        setLimitationRules(newLimitationRules)
      }
    }

    if (chosenDevice) {
      const newLimitationRules = adjustChosenDeviceLimitationRules(
        limitationRules,
        device,
        'add',
      )

      if (newLimitationRules) {
        setLimitationRules(newLimitationRules)
      }
    }

    this.chosenDevice = device

    // hard clear accessories before loading new
    this.setAccessories(null)

    if (device && isEmpty(device.optionalDevices)) {
      await this.fetchOrderAccessories()
    }

    if (device && !isEmpty(device.optionalDevices)) {
      this.setAccessories(device.optionalDevices || null)
    }
  }

  setPreviewColumnDevice = async (device: OrderDevice | null) => {
    this.previewColumnDevice = device
  }

  setChosenDeviceQuantity = (quantity: number) => {
    const { chosenDevice } = this

    if (chosenDevice) {
      const addonDevices = chosenDevice.addonDevices?.map(addonDevice => ({
        ...addonDevice,
        quantity,
      }))

      chosenDevice.quantity = quantity
      chosenDevice.addonDevices = addonDevices
    }
  }

  setChosenDeviceLabel = (label: string) => {
    const { chosenDevice } = this

    if (chosenDevice) {
      chosenDevice.customerLabel = label
    }
  }

  clearChosenAccessories = () => {
    const { chosenAccessories, limitationRules, setLimitationRules } = this

    if (this.isProductOffer) {
      for (const [key, accessory] of chosenAccessories) {
        if (!accessory.isCreateSeparateRental) chosenAccessories.delete(key)
      }
    }

    const newLimitationRules = adjustChosenAccessoriesLimitationRules(
      limitationRules,
      chosenAccessories,
      'add',
    )

    if (newLimitationRules) {
      setLimitationRules(newLimitationRules)
    }

    chosenAccessories.clear()
  }

  clearChosenDeviceWithAccessories = () => {
    const {
      chosenDevice,
      chosenAccessories,
      limitationRules,
      setLimitationRules,
    } = this

    let newLimitationRules = adjustChosenDeviceLimitationRules(
      limitationRules,
      chosenDevice,
      'add',
    )

    newLimitationRules = adjustChosenAccessoriesLimitationRules(
      limitationRules,
      chosenAccessories,
      'add',
    )

    if (newLimitationRules) {
      setLimitationRules(newLimitationRules)
    }

    this.setChosenDevice(null)
    chosenAccessories.clear()
  }

  clearChosenDeviceLimitationRules = () => {
    const { chosenDevice, limitationRules, setLimitationRules } = this

    const newLimitationRules = adjustChosenDeviceLimitationRules(
      limitationRules,
      chosenDevice,
      'add',
    )

    if (newLimitationRules) {
      setLimitationRules(newLimitationRules)
    }
  }

  toggleAccessorySelection = (accessory: OrderDevice) => {
    const { chosenAccessories, limitationRules, setLimitationRules } = this

    if (chosenAccessories.has(accessory.id)) {
      chosenAccessories.delete(accessory.id)

      if (this.isProductOffer && !accessory.isCreateSeparateRental) {
        return
      }

      const newLimitationRules = adjustChosenAccessoryLimitationRules(
        limitationRules,
        accessory,
        'add',
      )

      if (newLimitationRules) {
        setLimitationRules(newLimitationRules)
      }
    } else {
      chosenAccessories.set(accessory.id, accessory)

      if (this.isProductOffer && !accessory.isCreateSeparateRental) {
        return
      }

      const newLimitationRules = adjustChosenAccessoryLimitationRules(
        limitationRules,
        accessory,
        'remove',
      )

      if (newLimitationRules) {
        setLimitationRules(newLimitationRules)
      }
    }
  }

  setAccessoryQuantity = (quantity: number, accessory: OrderDevice) => {
    const { chosenAccessories } = this

    if (chosenAccessories.has(accessory.id)) {
      chosenAccessories.set(accessory.id, { ...accessory, quantity })
    }
  }

  setAccessoryLabel = (label: string, accessory: OrderDevice) => {
    const { chosenAccessories } = this

    if (chosenAccessories.has(accessory.id)) {
      chosenAccessories.set(accessory.id, {
        ...accessory,
        customerLabel: label,
      })
    }
  }

  setCurrentStep = (section: OrderStepEnum | null) => {
    this.currentStep = section
  }

  setLimitationRules = (limitations: OrderLimitations | null) => {
    this.limitationRules = limitations
  }

  setChosenShippingAddress = (
    shippingAddress:
      | getOrderEmployee_employee_office_address
      | getOrderOrganisation_organisation_offices_offices_address
      | null,
  ) => {
    this.chosenShippingAddress = shippingAddress
  }

  setChosenShippingAddressLabel = (label: string) => {
    this.chosenShippingAddressLabel = label
  }

  setChosenShippingAddressType = (addressType: AddressTypeEnum | null) => {
    this.chosenShippingAddressType = addressType
  }

  setRemarks = (remarks: string | null) => {
    this.remarks = remarks
  }

  setFilters = (filters: OrderFilters) => {
    this.filters = filters
  }

  clearFilters = () => {
    this.filters = new Map([
      [OrderFilterTypeEnum.Colors, new Set<string>()],
      [OrderFilterTypeEnum.Manufacturers, new Set<string>()],
      [OrderFilterTypeEnum.Storages, new Set<string>()],
      [OrderFilterTypeEnum.ProductTypes, new Set<string>()],
    ])
  }

  setSortCriteria = (sortCriteria: OrderSortCriteriaEnum) => {
    this.sortCriteria = sortCriteria
  }

  setAccessories = (accessories: OrderDevice[] | null) => {
    this.accessories = accessories
  }

  setOfficeAddress = (
    officeAddress:
      | getOrganisation_organisation_contact_address
      | getEmployee_employee_office_address
      | null,
  ) => {
    this.officeAddress = officeAddress
  }

  setFetchError = (isError: boolean) => {
    this.fetchError = isError
  }

  setMutationError = (isError: boolean) => {
    this.mutationError = isError
  }

  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading
  }

  setIsAccessoriesLoading = (isLoading: boolean) => {
    this.isAccessoriesLoading = isLoading
  }

  setIsCYOD = (isCYOD: boolean) => {
    this.isCYOD = isCYOD
  }

  setIsProductOffer = (isProductOffer: boolean) => {
    this.isProductOffer = isProductOffer
  }

  setIsOrderDisabled = (isOrderDisabled: boolean) => {
    this.isOrderDisabled = isOrderDisabled
  }

  setOrderedDevices = (
    devices:
      | mutationNewDeviceCaseItem_createNewDeviceCaseItem_subCaseItems[]
      | number
      | null,
  ) => {
    this.orderedDevices = devices
  }

  clearAllChoices = () => {
    this.clearChosenDeviceWithAccessories()
    this.setOrderedDevices(null)
    this.setChosenShippingAddress(null)
    this.setCurrentStep(OrderStepEnum.device)
    this.clearFilters()
    this.setRemarks(null)
    this.setSortCriteria(OrderSortCriteriaEnum.Default)
    this.setEmployeeContact(null)
  }

  resetAll = () => {
    this.clearAllChoices()
    this.setChosenDevice(null)
    this.setAccessories(null)
    this.setOfficeAddress(null)
    this.setOrderDevices(null)
    this.setFetchError(false)
    this.setIsLoading(false)
    this.setLimitationRules(null)
    this.setMutationError(false)
    this.setNetPriceCalculator(NetPriceCalculatorDefaultValues)
    this.setOriginRoute(null)
    this.setIsOrderDisabled(false)
  }

  setNetPriceCalculator = (
    netPriceCalculatorValue: NetPriceCalculatorInput,
  ) => {
    this.netPriceCalculator = netPriceCalculatorValue
  }

  setEmployeeContact = (
    contact: getEmployeeContact_employee_contact | null,
  ) => {
    this.employeeContact = contact
  }

  fetchEmployeeContact = async (id: string) => {
    const { apolloClient } = ApolloLoader

    if (!apolloClient) return

    const response = await apolloClient.query<
      getEmployeeContact,
      getEmployeeContactVariables
    >({
      query: GET_EMPLOYEE_CONTACT,
      variables: {
        id,
      },
    })

    const employeeOfficeAddress =
      response.data.employee.office?.address ||
      response.data.employee.organisation?.contact?.address

    this.setEmployeeContact(response.data.employee.contact)
    this.setOfficeAddress(employeeOfficeAddress || null)
  }

  setOriginRoute = (originRoute: OriginRoute) => {
    this.originRoute = originRoute
  }

  get getTotalPrice() {
    const { chosenAccessories, chosenDevice, previewColumnDevice } = this

    if (!chosenDevice && !previewColumnDevice) {
      return null
    }

    const device = chosenDevice || previewColumnDevice

    const quantity = device?.quantity ?? 1
    const primaryDeviceCopaymentNet = device?.copaymentNet ?? 0
    const primaryDeviceCalculatedNet = device?.calculatedNet ?? 0
    const primaryCopaymentGross = device?.copaymentGross ?? 0
    const primaryBasisAmount = device?.basisAmount ?? 0

    const addonDevicesCopaymentNet =
      device?.addonDevices?.reduce(
        (finalCopaymentNet, currentAddonDevice) =>
          finalCopaymentNet + currentAddonDevice.copaymentNet,
        0,
      ) ?? 0

    const addonDevicesCopaymentGross =
      device?.addonDevices?.reduce(
        (finalCopaymentGross, currentAddonDevice) =>
          finalCopaymentGross + currentAddonDevice.copaymentGross!,
        0,
      ) ?? 0

    const addonDevicesBasisAmount =
      device?.addonDevices?.reduce(
        (finalBasisAmount, currentAddonDevice) =>
          finalBasisAmount + currentAddonDevice.basisAmount!,
        0,
      ) ?? 0

    const addonDevicesCalculatedNet =
      device?.addonDevices?.reduce(
        (finalCalculatedNet, currentAddonDevice) =>
          finalCalculatedNet + (currentAddonDevice.calculatedNet ?? 0),
        0,
      ) ?? 0

    const copaymentNet = primaryDeviceCopaymentNet + addonDevicesCopaymentNet
    const copaymentGross = primaryCopaymentGross + addonDevicesCopaymentGross
    const basisAmount = primaryBasisAmount + addonDevicesBasisAmount
    const calculatedNet = primaryDeviceCalculatedNet + addonDevicesCalculatedNet

    let totalWithoutTax = copaymentNet * quantity
    let totalWithTax = copaymentGross * quantity
    let totalCalculatedNet = calculatedNet * quantity
    let totalBasis = basisAmount * quantity

    for (const [_, accessory] of chosenAccessories) {
      if (accessory.copaymentGross) {
        totalWithTax += accessory.copaymentGross * accessory.quantity
      }

      if (accessory.copaymentNet) {
        totalWithoutTax += accessory.copaymentNet * accessory.quantity
      }

      if (accessory.calculatedNet) {
        totalCalculatedNet += accessory.calculatedNet * accessory.quantity
      }

      if (accessory.basisAmount) {
        totalBasis += accessory.basisAmount * accessory.quantity
      }
    }

    return { totalBasis, totalCalculatedNet, totalWithTax, totalWithoutTax }
  }

  public get shouldSkipAccessories() {
    const { accessories, limitationRules } = this

    if (
      this.isProductOffer &&
      accessories?.some(accessory => !accessory?.isCreateSeparateRental)
    ) {
      return false
    }

    return (
      // accessories not available
      !accessories?.length ||
      // cannot order more because of limitation rules
      (limitationRules && limitationRules.other.initial <= 0)
    )
  }
}
